/**
 * Wijk- en buurtcijfers voor SportCity (MA-73): de CBS Kerncijfers wijken en
 * buurten 2024, met de vlakken, voor één gemeente tegelijk.
 *
 * Landelijk, dus voor elke gemeente gelijk; de Amsterdamse laag Buurten en cijfers
 * (MA-55) leest het Amsterdamse BBGA en werkt daarbuiten niet. De API haalt ze
 * per gemeente bij PDOK (`cbs/wijken-en-buurten-2024`) en houdt ze vast.
 *
 * Grens: het inkomen staat in deze uitgave nog leeg (-99997), net als opleiding.
 */
import { z } from 'zod';

export const WIJKCIJFERS_JAAR = 2024;

export const WIJK_NIVEAUS = ['wijk', 'buurt'] as const;
export const wijkNiveauSchema = z.enum(WIJK_NIVEAUS);
export type WijkNiveau = z.infer<typeof wijkNiveauSchema>;

/**
 * De cijfers die we tonen, met het PDOK-veld waar ze vandaan komen. De volgorde is
 * die van het popupje: eerst wie er wonen, dan hoe, dan wat er in de buurt is.
 */
export const WIJKCIJFERS = [
  { id: 'inwoners', veld: 'aantal_inwoners', label: 'Inwoners', eenheid: '' },
  {
    id: 'dichtheid',
    veld: 'bevolkingsdichtheid_inwoners_per_km2',
    label: 'Inwoners per km²',
    eenheid: '',
  },
  { id: 'jong', veld: 'percentage_personen_15_tot_25_jaar', label: '15 tot 25 jaar', eenheid: '%' },
  {
    id: 'jongvolwassen',
    veld: 'percentage_personen_25_tot_45_jaar',
    label: '25 tot 45 jaar',
    eenheid: '%',
  },
  {
    id: 'middelbaar',
    veld: 'percentage_personen_45_tot_65_jaar',
    label: '45 tot 65 jaar',
    eenheid: '%',
  },
  {
    id: 'ouder',
    veld: 'percentage_personen_65_jaar_en_ouder',
    label: '65 jaar en ouder',
    eenheid: '%',
  },
  { id: 'huishoudens', veld: 'aantal_huishoudens', label: 'Huishoudens', eenheid: '' },
  {
    id: 'eenpersoons',
    veld: 'percentage_eenpersoonshuishoudens',
    label: 'Eenpersoonshuishoudens',
    eenheid: '%',
  },
  {
    id: 'kinderen',
    veld: 'percentage_huishoudens_met_kinderen',
    label: 'Huishoudens met kinderen',
    eenheid: '%',
  },
  {
    id: 'huishoudgrootte',
    veld: 'gemiddelde_huishoudsgrootte',
    label: 'Gemiddelde huishoudgrootte',
    eenheid: '',
  },
  {
    id: 'woningwaarde',
    veld: 'gemiddelde_woningwaarde',
    label: 'Gemiddelde WOZ-waarde',
    eenheid: '× € 1.000',
  },
  { id: 'koop', veld: 'percentage_koopwoningen', label: 'Koopwoningen', eenheid: '%' },
  {
    id: 'stedelijkheid',
    veld: 'stedelijkheid_adressen_per_km2',
    label: 'Stedelijkheid (1 = zeer sterk, 5 = niet)',
    eenheid: '',
  },
  {
    id: 'autos',
    veld: 'personenautos_per_huishouden',
    label: "Auto's per huishouden",
    eenheid: '',
  },
  {
    id: 'bedrijven',
    veld: 'aantal_bedrijfsvestigingen',
    label: 'Bedrijfsvestigingen',
    eenheid: '',
  },
  {
    id: 'zwembad',
    veld: 'zwembad_gemiddelde_afstand_in_km',
    label: 'Afstand tot zwembad',
    eenheid: 'km',
  },
  {
    id: 'sauna',
    veld: 'sauna_gemiddelde_afstand_in_km',
    label: 'Afstand tot sauna',
    eenheid: 'km',
  },
  {
    id: 'station',
    veld: 'treinstation_gemiddelde_afstand_in_km',
    label: 'Afstand tot treinstation',
    eenheid: 'km',
  },
] as const;

export type WijkcijferId = (typeof WIJKCIJFERS)[number]['id'];
export const WIJKCIJFER_IDS = WIJKCIJFERS.map((cijfer) => cijfer.id) as [
  WijkcijferId,
  ...WijkcijferId[],
];
export const WIJKCIJFER_STANDAARD: WijkcijferId = 'jongvolwassen';

const positie = z.array(z.number());
const vlakSchema = z.union([
  z.object({ type: z.literal('Polygon'), coordinates: z.array(z.array(positie)) }),
  z.object({ type: z.literal('MultiPolygon'), coordinates: z.array(z.array(z.array(positie))) }),
]);

export const wijkvlakSchema = z.object({
  type: z.literal('Feature'),
  geometry: vlakSchema,
  properties: z.object({
    code: z.string(),
    naam: z.string(),
    /** Bij een buurt: de wijk waar hij in ligt. */
    wijk: z.string().nullable(),
    /** Per cijfer de waarde, of null als het CBS hem niet geeft (geheim of te klein). */
    cijfers: z.record(z.string(), z.number().nullable()),
  }),
});
export type Wijkvlak = z.infer<typeof wijkvlakSchema>;

export const wijkcijfersSchema = z.object({
  gemeente: z.object({ code: z.string(), naam: z.string() }),
  jaar: z.number().int(),
  wijken: z.object({ type: z.literal('FeatureCollection'), features: z.array(wijkvlakSchema) }),
  buurten: z.object({ type: z.literal('FeatureCollection'), features: z.array(wijkvlakSchema) }),
});
export type Wijkcijfers = z.infer<typeof wijkcijfersSchema>;

/**
 * Grenzen voor vijf klassen met elk ongeveer evenveel vlakken (kwintielen), voor de
 * kleur op de kaart. Minder verschillende waarden dan klassen: dan minder grenzen.
 */
export function klassegrenzen(waarden: readonly (number | null)[], klassen = 5): number[] {
  const gesorteerd = waarden.filter((w): w is number => w !== null).sort((a, b) => a - b);
  const laagste = gesorteerd[0];
  if (laagste === undefined) return [];
  const grenzen: number[] = [];
  for (let i = 1; i < klassen; i++) {
    const grens = gesorteerd[Math.floor((i * gesorteerd.length) / klassen)] as number;
    if (grens > laagste && !grenzen.includes(grens)) grenzen.push(grens);
  }
  return grenzen;
}
